import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SEO from '../../../../shared/components/SEO/SEO';
import { useAuth } from '../../../../contexts/AuthContext';
import { forumAPI } from '../../../../services/api';
import { getCategories, getPosts, type ForumCategory, type ForumPost } from './communityApi';
import { stripHtml } from './utils';
import { UserAvatar } from './UserAvatar';
import './Community.css';

const CommunityHome: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [loading, setLoading] = useState(true);
  const pageSize = 20;

  useEffect(() => {
    getCategories()
      .then((res) => setCategories(res.data))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params: { category?: string; search?: string; page: number; page_size: number } = {
      page,
      page_size: pageSize,
    };
    if (search) params.search = search;
    getPosts(params)
      .then((res) => {
        setPosts(res.data.results);
        setCount(res.data.count);
      })
      .catch(() => {
        setPosts([]);
        setCount(0);
      })
      .finally(() => setLoading(false));
  }, [search, page]);

  const totalPages = Math.ceil(count / pageSize) || 1;
  const showPendingNotice = searchParams.get('pending') === '1';

  const handleLike = useCallback(
    (e: React.MouseEvent, postId: number) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isAuthenticated) {
        navigate('/login?next=' + encodeURIComponent('/community'));
        return;
      }
      forumAPI
        .likePost(postId)
        .then((res) => {
          setPosts((prev) =>
            prev.map((p) =>
              p.id === postId ? { ...p, liked: res.data.liked, like_count: res.data.like_count } : p
            )
          );
        })
        .catch(() => {});
    },
    [isAuthenticated, navigate]
  );

  return (
    <>
      <SEO
        title="Community - Crypxa Forum"
        description="Discuss coins, predictions, and strategies with the Crypxa community."
        keywords="Crypxa community, crypto forum, predictions discussion"
      />
      <div className="community-page">
        <div className="community-header">
          <h1>Community</h1>
          {isAuthenticated ? (
            <Link to="/community/new" className="community-btn">+ New post</Link>
          ) : (
            <Link to={'/login?next=' + encodeURIComponent('/community/new')} className="community-btn">Log in to post</Link>
          )}
        </div>
        <div className="community-categories">
          {isAuthenticated && (
            <Link to="/community/my-posts" className="community-cat-link community-my-posts">My posts</Link>
          )}
          <Link to="/community" className="community-cat-link active">All</Link>
          {categories.map((cat) => (
            <Link key={cat.id} to={`/community/c/${cat.slug}`} className="community-cat-link">
              {cat.name} ({cat.post_count})
            </Link>
          ))}
        </div>
        {showPendingNotice && (
          <div className="community-pending-notice" role="alert">
            Your post has been submitted and is pending approval. It will appear here once an admin approves it.
          </div>
        )}
        <div className="community-search">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearch((e.currentTarget.elements.namedItem('q') as HTMLInputElement)?.value || '');
            }}
          >
            <input
              type="search"
              name="q"
              placeholder="Search posts..."
              defaultValue={search}
            />
          </form>
        </div>
        {loading ? (
          <div className="community-empty">Loading...</div>
        ) : posts.length === 0 ? (
          <div className="community-empty">No posts yet. Be the first to start a discussion!</div>
        ) : (
          <>
            <div className="community-post-list">
              {posts.map((post) => (
                <Link key={post.id} to={`/community/p/${post.id}`} className="community-post-card">
                  <div className="community-card-media">
                    {post.attachments && post.attachments.length > 0 ? (
                      <img src={post.attachments[0].file_url} alt="" />
                    ) : (
                      <div className="community-card-placeholder">
                        <span className="community-card-placeholder-icon" aria-hidden>🖼️</span>
                        <span>No image</span>
                      </div>
                    )}
                  </div>
                  <div className="community-card-body">
                    <div className="meta">
                      {post.is_pinned && <span className="pinned">Pinned</span>}
                      <UserAvatar user={post.user} />
                      <span>{post.user.display_name}</span>
                      <span>{new Date(post.created_at).toLocaleDateString()}</span>
                    </div>
                    <h3>{post.title}</h3>
                    <p className="excerpt">{stripHtml(post.body).slice(0, 180)}{stripHtml(post.body).length > 180 ? '...' : ''}</p>
                    {post.attachments && post.attachments.length > 1 && (
                      <div className="community-card-images">
                        {post.attachments.slice(1, 5).map((att) => (
                          <span key={att.id} className="community-card-thumb">
                            <img src={att.file_url} alt="" />
                          </span>
                        ))}
                        {post.attachments.length > 5 && (
                          <span className="community-card-thumb community-card-thumb-more">+{post.attachments.length - 5}</span>
                        )}
                      </div>
                    )}
                    <div className="stats">
                      <button type="button" className={`community-stat-item stat-likes ${post.liked ? 'liked' : ''}`} onClick={(e) => handleLike(e, post.id)} aria-label={post.liked ? 'Unlike' : 'Like'}>
                        {(post.like_count ?? 0) > 0 || post.liked ? <i className={`fas fa-heart${post.liked ? '' : '-o'}`} aria-hidden></i> : null} {post.like_count ?? 0}
                      </button>
                      <span className="community-stat-item stat-comments"><i className="fas fa-comment" aria-hidden></i> {post.comment_count ?? 0}</span>
                      <button
                        type="button"
                        className="community-stat-item stat-share"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigator.clipboard?.writeText(`${window.location.origin}/community/p/${post.id}`);
                        }}
                        aria-label="Copy link"
                      >
                        <i className="fas fa-share-alt" aria-hidden></i> Share
                      </button>
                      <span className="community-stat-item stat-views"><i className="fas fa-eye" aria-hidden></i> {post.views}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="community-pagination">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </button>
                <span style={{ alignSelf: 'center', fontSize: '0.9rem' }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default CommunityHome;
